<template>
  <div class="google-map-container">
    <!-- 地图容器 -->
    <div ref="mapContainer" class="map-container"></div>
    <!-- 错误信息显示 -->
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  </div>
</template>
<script>
export default {
  name: 'GoogleMap',
  props: {
    // 谷歌地图 API 密钥（需要用户自行提供）
    apiKey: {
      type: String,
      required: true
    },
    // 初始纬度
    lat: {
      type: Number,
      default: 39.9042 // 北京纬度
    },
    // 初始经度
    lng: {
      type: Number,
      default: 116.4074 // 北京经度
    },
    // 初始缩放级别
    zoom: {
      type: Number,
      default: 12
    },
    // 标记点标题
    markerTitle: {
      type: String,
      default: 'Marker'
    },
    // Store Name/地址/电话：用于点击标记点时展示弹窗内容
    locationName: {
      type: String,
      default: ''
    },
    locationAddress: {
      type: String,
      default: ''
    },
    locationPhone: {
      type: String,
      default: ''
    },
    // 是否展示标记点弹窗
    showInfoWindow: {
      type: Boolean,
      default: true
    },
    // 是否允许拖动标记点（店铺详情页一般不需要拖动）
    markerDraggable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      map: null,
      marker: null,
      infoWindow: null,
      errorMessage: ''
    }
  },
  watch: {
    lat() {
      this.tryUpdateCenter()
      this.tryUpdateInfoWindowContent()
    },
    lng() {
      this.tryUpdateCenter()
      this.tryUpdateInfoWindowContent()
    },
    locationName() {
      this.tryUpdateInfoWindowContent()
    },
    locationAddress() {
      this.tryUpdateInfoWindowContent()
    },
    locationPhone() {
      this.tryUpdateInfoWindowContent()
    }
  },
  mounted() {
    // 加载谷歌地图脚本
    this.loadGoogleMapScript()
      .then(() => {
        // 脚本加载成功后初始化地图
        this.initMap()
      })
      .catch(error => {
        this.errorMessage = `Map failed to load: ${error.message}`
        console.error('Map failed to load:', error)
      })
  },
  methods: {
    escapeHtml(value) {
      const s = String(value == null ? '' : value)
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    getNavigationUrl() {
      const lat = Number(this.lat)
      const lng = Number(this.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'https://www.google.com/maps'
      // Let Google Maps use the user's current location as origin by default.
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${lat},${lng}`
      )}&travelmode=driving`
    },
    buildInfoWindowContent() {
      const name = this.escapeHtml(this.locationName || this.markerTitle || 'Location')
      const address = this.escapeHtml(this.locationAddress || '')
      const navUrl = this.getNavigationUrl()
      const addressHtml = address ? `<div style="margin: 0 0 8px 0; color: #666; font-size: 12px; line-height: 1.4;">${address}</div>`
        : ''
      return `
        <div style="padding: 8px; min-width: 160px; max-width: 200px; box-sizing: border-box;">
          <div style="margin: 0 0 6px 0; color: #333; font-weight: 600; font-size: 14px; word-break: break-word;">${name}</div>
          ${address ? `<div style="margin: 0 0 6px 0; color: #666; font-size: 12px; line-height: 1.4; word-break: break-word;">${address}</div>`
            : ''}
          <div style="margin-top: 6px;">
            <a href="${navUrl}"
               target="_blank"
               rel="noopener noreferrer"
               style="display: inline-block; background: #4285f4; color: #fff; padding: 6px 12px; text-decoration: none; border-radius: 4px; font-size: 12px;">
              Navigate
            </a>
          </div>
        </div>
      `
    },
    ensureInfoWindow() {
      if (!this.showInfoWindow || !window.google || !window.google.maps) return null
      const content = this.buildInfoWindowContent()
      if (!this.infoWindow) {
        this.infoWindow = new window.google.maps.InfoWindow({ content })
      } else {
        this.infoWindow.setContent(content)
      }
      return this.infoWindow
    },
    openInfoWindow() {
      const iw = this.ensureInfoWindow()
      if (!iw || !this.map || !this.marker) return
      iw.open(this.map, this.marker)
    },
    closeInfoWindow() {
      if (this.infoWindow) this.infoWindow.close()
    },
    // 动态加载谷歌地图脚本
    loadGoogleMapScript() {
      return new Promise((resolve, reject) => {
        // 检查脚本是否已加载
        if (window.google && window.google.maps) {
          resolve()
          return
        }
        // 创建 script 标签
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&callback=initGoogleMap`
        script.async = true
        script.defer = true
        // 定义回调函数
        window.initGoogleMap = () => {
          resolve()
        }
        // 错误处理
        script.onerror = () => {
          reject(new Error('Unable to load Google Maps script'))
        }
        // 添加到页面
        document.head.appendChild(script)
      })
    },
    // 初始化地图
    initMap() {
      // 创建地图实例
      this.map = new window.google.maps.Map(this.$refs.mapContainer, {
        center: { lat: this.lat, lng: this.lng },
        zoom: this.zoom,
        // 只展示地图与标记点：关闭多余 UI 控件
        disableDefaultUI: true,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        zoomControl: false,
        rotateControl: false,
        scaleControl: false,
        keyboardShortcuts: false,
        clickableIcons: false,
        // 可以添加其他地图选项，如地图类型等
        mapTypeId: window.google.maps.MapTypeId.ROADMAP
      })
      // 添加标记
      this.addMarker()
      // 点击地图空白处关闭弹窗
      this.map.addListener('click', () => {
        this.closeInfoWindow()
      })
      // 监听地图移动事件
      this.map.addListener('center_changed', () => {
        // 可以在这里获取地图中心的新坐标
        const center = this.map.getCenter()
        this.$emit('center-changed', {
          lat: center.lat(),
          lng: center.lng()
        })
      })
    },
    // 添加标记
    addMarker() {
      // 如果已有标记，先移除
      if (this.marker) {
        this.marker.setMap(null)
      }
      // 创建新标记
      this.marker = new window.google.maps.Marker({
        position: { lat: this.lat, lng: this.lng },
        map: this.map,
        title: this.markerTitle,
        draggable: !!this.markerDraggable
      })
      // 点击标记点：展示Store Info + 导航按钮
      this.marker.addListener('click', () => {
        this.openInfoWindow()
      })
      // 标记拖动结束事件（仅在启用拖动时）
      if (this.markerDraggable) {
        this.marker.addListener('dragend', (event) => {
          this.$emit('marker-moved', {
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
          })
        })
      }
      // 预先准备弹窗内容（不自动打开）
      this.ensureInfoWindow()
    },
    // 更新地图中心位置
    setCenter(lat, lng) {
      if (this.map) {
        this.map.setCenter({ lat, lng })
        // 同时移动标记
        this.marker.setPosition({ lat, lng })
      }
    },
    // props 异步更新时，尝试刷新地图中心点
    tryUpdateCenter() {
      if (!this.map || !this.marker) return
      const lat = Number(this.lat)
      const lng = Number(this.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
      this.setCenter(lat, lng)
    },
    tryUpdateInfoWindowContent() {
      if (!this.map || !this.marker) return
      // Only update content; do not auto-open.
      this.ensureInfoWindow()
    }
  }
}
</script>
<style scoped>
.google-map-container {
  position: relative;
  width: 100%;
  height: 100%;
  margin-bottom: 1rem;
}
.map-container {
  width: 100%;
  height: 200px; /* 可根据需要调整高度 */
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
.error-message {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  padding: 10px;
  background-color: #ff4444;
  color: white;
  border-radius: 4px;
  text-align: center;
}
</style>
<style>
/* 覆盖谷歌地图 InfoWindow 关闭按钮的高度 */
.gm-ui-hover-effect {
  width: 32px !important;
  height: 32px !important;
}
.gm-ui-hover-effect > span {
  margin: 4px !important;
}
</style>
